import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader, Users, X } from "lucide-react";
import { useDispatch } from "react-redux";
import DualListBox from "./DualListBox";
import ToastMessage from "../../../../utils/toasts/toastMessage";
import {
  createCohort,
  updateCohort,
} from "../../../../Redux/Teacher_Panel/cohort/cohort.action";
import { getCohortGroups } from "../../../../Redux/Admin_Panel/cohort/cohort.action";

const CreateCohortModal = ({
  show,
  onHide,
  classOptions = [],
  classDivisionMap = {},
  teacherClasses = [],
  cohortData = null,
  editing = false,
  viewOnly = false,
  teacherSchoolName,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const [cohortName, setCohortName] = useState("");
  const [selectedClassDivision, setSelectedClassDivision] = useState(""); // "grade|division"
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [memberIds, setMemberIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const dropdownRef = useRef(null);

  // Reset / prefill when modal opens
  useEffect(() => {
    if (!show) return;
      setToastState({ show: false, message: "", type: "" });
    if (editing && cohortData) {
      setCohortName(cohortData.group_name || cohortData.name || "");
      setSelectedClassDivision(
        cohortData.grade && cohortData.division
          ? `${cohortData.grade}|${cohortData.division}`
          : "",
      );
      setMemberIds(cohortData.student_id || cohortData.student_ids || []);
    } else {
      setCohortName("");
      setSelectedClassDivision("");
      setMemberIds([]);
    }
    setDropdownOpen(false);
  }, [show, editing, cohortData]);

  // Close dropdown on outside click / Escape
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [dropdownOpen]);

  const classDivisionStudentsMap = useMemo(() => {
    const map = {};
    teacherClasses.forEach((c) => {
      map[`${c.class}|${c.division}`] = c.students || [];
    });
    return map;
  }, [teacherClasses]);

  // Flat list of class-division options, e.g. { value: "8|A", label: "8A", count }
  const classDivisionOptions = useMemo(() => {
    const options = [];
    Object.keys(classDivisionMap).forEach((grade) => {
      const gradeLabel =
        classOptions.find((c) => c.value === grade)?.label || grade;
      Object.keys(classDivisionMap[grade] || {}).forEach((division) => {
        const value = `${grade}|${division}`;
        options.push({
          value,
          label: `${gradeLabel}${division}`,
          classId: grade,
          divisionId: division,
          count: (classDivisionStudentsMap[value] || []).length,
        });
      });
    });
    return options;
  }, [classDivisionMap, classOptions, classDivisionStudentsMap]);

  const classStudents = useMemo(() => {
    if (!selectedClassDivision) return [];
    return classDivisionStudentsMap[selectedClassDivision] || [];
  }, [selectedClassDivision, classDivisionStudentsMap]);

  const selectClassDivision = (value) => {
    setSelectedClassDivision(value);
    setMemberIds([]);
    setDropdownOpen(false);
  };

  const showError = (message) =>
    setToastState({ show: true, message, type: "error" });

  const handleSave = async () => {
    if (viewOnly) return; // read-only guard
    if (!cohortName.trim()) return showError("Cohort name is required.");
    if (!selectedClassDivision) return showError("Class is required.");
    if (memberIds.length === 0) return showError("Select at least one member.");

    setSaving(true);

    const [grade, division] = selectedClassDivision.split("|");

    try {
      let result;

      const savedCohort = {
        group_name: cohortName,
        student_id: memberIds,
        grade,
        division,
        school_name: teacherSchoolName ? teacherSchoolName : "",
      };

      if (editing) {
        result = await dispatch(updateCohort(cohortData?.id, savedCohort));
      } else {
        result = await dispatch(createCohort(savedCohort));
      }
      if (result?.success === true) {
        setToastState({
          show: true,
          message:
            result?.message ||
            (editing
              ? "Cohort updated successfully."
              : "Cohort created successfully."),
          type: "success",
        });
        dispatch(getCohortGroups());
        setTimeout(() => {
          onHide?.();
        }, 500);
      } else {
        showError(
          result?.message ||
            (editing
              ? "Could not update cohort. Please try again."
              : "Could not create cohort. Please try again."),
        );
      }
    } catch {
      showError("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const selectedLabel =
    classDivisionOptions.find((o) => o.value === selectedClassDivision)
      ?.label || selectedClassDivision.replace("|", "");

  const classDisabled = editing || viewOnly;

  return (
    <div
      className="modal fade show cohort-modal"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cm-title"
      style={{ display: "block", background: "rgba(16,24,40,0.55)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg cm-dialog">
        <div className="modal-content cm-content">
          {/* Header */}
          <div className="cm-header">
            <div>
              <h4 id="cm-title" className="cm-title">
                {viewOnly
                  ? "View Cohort"
                  : editing
                    ? "Edit Cohort"
                    : "Create New Cohort"}
              </h4>
              <p className="cm-subtitle">
                {viewOnly
                  ? "You can only view members of cohorts created by other teachers."
                  : editing
                    ? "Update who belongs to this cohort."
                    : "Choose a class, then pick the members."}
              </p>
            </div>
            <button
              type="button"
              className="cm-close"
              onClick={onHide}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="cm-body">
            <div className="cm-form-grid">
              {/* Class */}
              <div className="cm-field" ref={dropdownRef}>
                <span className="cm-label">Class</span>
                <button
                  type="button"
                  className={`cm-control cm-select ${
                    dropdownOpen ? "is-open" : ""
                  } ${classDisabled ? "is-disabled" : ""}`}
                  onClick={() => setDropdownOpen((o) => !o)}
                  disabled={classDisabled}
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                >
                  <span
                    className={`cm-select-value ${
                      selectedClassDivision ? "" : "is-placeholder"
                    }`}
                  >
                    {selectedClassDivision ? selectedLabel : "Select class"}
                  </span>
                  <ChevronDown size={16} className="cm-chevron" />
                </button>

                {dropdownOpen && (
                  <div className="cm-menu" role="listbox">
                    {classDivisionOptions.length === 0 && (
                      <div className="cm-empty">No classes available</div>
                    )}
                    {classDivisionOptions.map((option) => (
                      <button
                        type="button"
                        key={option.value}
                        role="option"
                        aria-selected={selectedClassDivision === option.value}
                        className={`cm-option ${
                          selectedClassDivision === option.value
                            ? "is-selected"
                            : ""
                        }`}
                        onClick={() => selectClassDivision(option.value)}
                      >
                        <span className="cm-option-label">{option.label}</span>
                        <span className="cm-option-meta">
                          {option.count} member{option.count === 1 ? "" : "s"}
                        </span>
                        {selectedClassDivision === option.value && (
                          <Check size={16} />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cohort name */}
              <div className="cm-field">
                <label className="cm-label" htmlFor="cm-name">
                  Cohort Name
                </label>
                <div
                  className={`cm-control ${
                    editing || viewOnly ? "is-disabled" : ""
                  }`}
                >
                  <input
                    id="cm-name"
                    type="text"
                    className="cm-input"
                    placeholder="e.g. Maths Advanced Batch"
                    maxLength={60}
                    value={cohortName}
                    onChange={(e) => setCohortName(e.target.value)}
                    disabled={editing || viewOnly}
                  />
                </div>
              </div>
            </div>

            {/* Members */}
            <div className="cm-section-head"></div>

            {selectedClassDivision ? (
              <div
                style={
                  viewOnly
                    ? { pointerEvents: "none", opacity: 0.85 }
                    : undefined
                }
                aria-disabled={viewOnly}
              >
                <DualListBox
                  allUsers={classStudents}
                  currentIds={memberIds}
                  onChange={viewOnly ? () => {} : setMemberIds}
                  leftTitle="Current Cohort Members"
                  rightTitle="Available Members"
                />
              </div>
            ) : (
              <div className="cm-placeholder">
                <Users size={28} />
                <strong>Pick a class</strong>
                <span>Members from the selected class will appear here.</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="cm-footer">
            <span className="cm-count">
              {memberIds.length > 0
                ? `${memberIds.length} member${
                    memberIds.length === 1 ? "" : "s"
                  } ${viewOnly ? "in cohort" : "selected"}`
                : "No members selected yet"}
            </span>
            <div className="cm-footer-actions">
              <button
                type="button"
                className="cm-btn cm-btn--ghost"
                onClick={onHide}
              >
                {viewOnly ? "Close" : "Cancel"}
              </button>

              {!viewOnly && (
                <button
                  type="button"
                  className="cm-btn cm-btn--primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader size={16} className="animate-spin-custom" />
                      Saving…
                    </>
                  ) : editing ? (
                    "Save Changes"
                  ) : (
                    "Create Cohort"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </div>
  );
};

export default CreateCohortModal;
